'use client';
import React, { ReactNode } from 'react';
import { antdThemeConfig } from '../utils/theme';
import {
  isServer,
  QueryClient,
  QueryClientProvider,
  keepPreviousData,
} from '@tanstack/react-query';
import { ConfigProvider, App } from 'antd';
import AntdRegistry from './AntdRegistry';
import { ToastProvider } from './ToastProvider';
import { AuthProvider } from './AuthProvider';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
        placeholderData: keepPreviousData,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export default function Providers({
  children,
}: Readonly<{ children: ReactNode }>) {
  const queryClient = getQueryClient();

  return (
    <AntdRegistry>
      <ConfigProvider theme={antdThemeConfig}>
        <App>
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <ToastProvider>{children}</ToastProvider>
            </QueryClientProvider>
          </AuthProvider>
        </App>
      </ConfigProvider>
    </AntdRegistry>
  );
}
